import { useState } from "react";
import { ActionMenuItem } from "../../../../components/actionMenu/ActionMenu";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../../helpers/apiHelper";
import {
  getDownloadRecallDocument,
  getRecallEntry,
  postGenerateRecallDocument,
} from "../../../../redux/api/recall.api";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { RecallClientActionsDataInterface } from "../../../../redux/interfaces/recall.interface";
import { canGenerateRecallDocument } from "./recallClientActionsHelper";

function useRecallClientDocumentAction({
  client,
}: {
  client: RecallClientActionsDataInterface;
}): ActionMenuItem {
  const dispatch = useAppDispatch();
  const isBatchLoading = useAppSelector(
    (state) => state.recall.clientBatch.isLoading,
  );
  const hasPendingClientActions = useAppSelector(
    (state) => state.recall.pendingClientActionIds.length > 0,
  );
  const isEntryLoading = useAppSelector(
    (state) => state.recall.entry.isLoading,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const isBusy = isBatchLoading || hasPendingClientActions || isEntryLoading;
  const generateDisabledReason = isGenerating
    ? "Generarea documentului este in curs."
    : isEntryLoading
    ? "Se incarca datele retragerii."
    : isBatchLoading || hasPendingClientActions
    ? "Asteapta finalizarea actiunii in curs pentru farmacii."
    : !canGenerateRecallDocument(client)
    ? "Generarea documentului nu este disponibila in starea curenta."
    : "";

  const handleGenerate = async () => {
    if (isGenerating || isBusy || !canGenerateRecallDocument(client)) return;
    setIsGenerating(true);
    const resultAction = await dispatch(
      postGenerateRecallDocument({
        idCampaign: client.idCampaign,
        clientPlId: client.clientPlId,
      }),
    );
    const status = checkEndpointStatus(
      resultAction,
      postGenerateRecallDocument,
    );

    if (status !== endpointStatus.pending) {
      if (status === endpointStatus.fulfilled) {
        await dispatch(getRecallEntry({ id: client.idCampaign }));
      }
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!client.document || isDownloading) return;
    setIsDownloading(true);
    const resultAction = await dispatch(
      getDownloadRecallDocument({
        idCampaign: client.idCampaign,
        clientPlId: client.clientPlId,
        fileName: client.document.filename,
      }),
    );

    if (
      checkEndpointStatus(resultAction, getDownloadRecallDocument) !==
      endpointStatus.pending
    ) {
      setIsDownloading(false);
    }
  };

  return client.document
    ? {
        id: "download-document",
        label: "Descarca document",
        icon: "DOCUMENTS",
        kind: "download",
        tooltip: isDownloading
          ? "Descarcarea documentului este in curs."
          : "Descarca PDF-ul documentului de retragere al farmaciei.",
        onClick: handleDownload,
        loading: isDownloading,
        disabled: isDownloading,
      }
    : {
        id: "generate-document",
        label: "Genereaza document",
        icon: "GENERATE_DOCUMENT",
        tooltip:
          generateDisabledReason ||
          "Genereaza documentul de retragere al farmaciei, disponibil apoi pentru descarcare.",
        onClick: handleGenerate,
        disabled: Boolean(generateDisabledReason),
        loading: isGenerating,
      };
}

export default useRecallClientDocumentAction;
